import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { UnifiedAIAssistant } from '@/components/UnifiedAIAssistant';
import { ArrowLeft, Sparkles } from 'lucide-react';
import { Bot } from '@/types/Bot';
import { supabase } from '@/integrations/supabase/client';

type BotRow = Record<string, unknown>;

const stringArray = (value: unknown) => Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];
const stringValue = (value: unknown, fallback = '') => typeof value === 'string' ? value : fallback;
const numberValue = (value: unknown, fallback: number) => Number(value ?? fallback);

const CACHE_KEY = 'vergo:bots-catalog:v1';
const CACHE_TTL_MS = 10 * 60 * 1000; // 10 minutes

const mapRow = (b: BotRow): Bot => ({
  id: stringValue(b.id),
  name: stringValue(b.name),
  description: stringValue(b.description),
  category: stringValue(b.use_case, 'General'),
  rating: numberValue(b.rating, 4.5),
  reviewCount: numberValue(b.conversations, 0),
  price: stringValue(b.pricing, 'Free') as Bot['price'],
  features: stringArray(b.key_features).length ? stringArray(b.key_features) : stringArray(b.features_array),
  tags: stringArray(b.tags),
  popularity: numberValue(b.fit_index, 50),
  imageUrl: stringValue(b.thumbnail, '/api/placeholder/400/300'),
  website: stringValue(b.website_url) || stringValue(b.site_url),
  modelType: stringValue(b.model_type) || undefined,
  loginRequired: Boolean(b.login_required),
  useCase: stringValue(b.use_case, 'General'),
});

const readCache = (): Bot[] | null => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { ts: number; bots: Bot[] };
    if (!parsed?.ts || Date.now() - parsed.ts > CACHE_TTL_MS) return null;
    return Array.isArray(parsed.bots) ? parsed.bots : null;
  } catch {
    return null;
  }
};

const writeCache = (bots: Bot[]) => {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), bots }));
  } catch {
    /* ignore quota */
  }
};

const AIRecommendations = () => {
  const [bots, setBots] = useState<Bot[]>(() => readCache() ?? []);

  useEffect(() => {
    // Skip network if cache is fresh
    if (bots.length > 0) return;

    let cancelled = false;
    const fetchBots = async () => {
      const { data } = await supabase
        .from('bots')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(60);

      if (!cancelled && data) {
        const mapped = (data as BotRow[]).map(mapRow);
        setBots(mapped);
        writeCache(mapped);
      }
    };
    fetchBots();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-background/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link
                to="/"
                className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Back to Home"
              >
                <ArrowLeft className="w-5 h-5" />
                <span>Back to Home</span>
              </Link>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-primary-foreground" />
              </div>
              <h1 className="text-xl font-bold text-foreground">
                Vergo
              </h1>
            </div>
          </div>
        </div>
      </header>

      {/* AI Assistant Page Content */}
      <main className="py-6 sm:py-12 px-4">
        <div className="container mx-auto max-w-5xl">
          <div className="text-center mb-6 sm:mb-10 space-y-3 sm:space-y-4 animate-fade-in">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20">
              <div className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
              <span className="text-xs font-medium text-primary">Powered by Gemini Pro</span>
            </div>

            <h1 className="text-3xl sm:text-5xl font-bold text-foreground">
              Find Your Perfect AI Tool
            </h1>

            <p className="text-sm sm:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed px-2">
              Describe what you need and get 5 personalized recommendations in seconds.
            </p>
          </div>


          <UnifiedAIAssistant availableBots={bots} />
        </div>
      </main>
    </div>
  );
};

export default AIRecommendations;
